import { useState } from 'react';

import PropTypes from 'prop-types';

import { useQuery } from '@zenbusiness/application-commons-graphql';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Checkbox from '@zenbusiness/zui/Checkbox';
import DialogContent from '@zenbusiness/zui/DialogContent';
import LoadingSpinner from '@zenbusiness/zui/LoadingSpinner';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import * as ROUTES from '#client/constants/routes';
import { useBillingSubscriptionChange } from '#client/hooks/useBillingSubscriptionChange';
import { PAYMENT_METHODS_QUERY } from '#client/hooks/useSubscriptionRestart';

import * as Styled from './ConfirmRestartSubscription.style';

import { useChangeRequestContext, useSetChangeRequest } from '../hooks/useChangeRequestContext';

export default function ConfirmRestartSubscription({ subscription, businessEntityName, serviceDisplayName }) {
  const [consentChecked, setConsentChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { formatMessage } = useIntl();
  const { changeFlow } = useChangeRequestContext();
  const { setChangeFlow } = useSetChangeRequest();
  const { modalFlow } = changeFlow;
  const { restartSubscription } = modalFlow;

  const { data: paymentData, loading: paymentLoading } = useQuery(PAYMENT_METHODS_QUERY, { noThrow: true });

  const { cancelSubscription: changeSubscription } = useBillingSubscriptionChange();

  const paymentMethod = paymentData?.paymentMethods
    ? (paymentData.paymentMethods.methods.find((m) => m.id === paymentData.paymentMethods.defaultMethodId) ??
      paymentData.paymentMethods.methods[0])
    : null;

  const cardInfo = !paymentLoading && paymentMethod ? `${paymentMethod.brand} x${paymentMethod.last4}` : '';

  const periodLabel =
    subscription?.period === 'monthly'
      ? formatMessage({ id: 'dashboard.subscriptions.price.periodMonth' })
      : formatMessage({ id: 'dashboard.subscriptions.price.periodYear' });

  const handleSubmit = async () => {
    if (!consentChecked || submitting) {
      return;
    }
    setSubmitting(true);

    try {
      const result = await changeSubscription({
        orderBillingItemUuid: subscription.orderBillingItemUuid,
        changeType: 'active',
      });
      const statusCode = result?.data?.billingSubscriptionChange?.statusCode;
      if (statusCode === 202) {
        setChangeFlow((prevState) => ({
          ...prevState,
          modalFlow: {
            ...modalFlow,
            restartSubscription: {
              ...restartSubscription,
              stepOne: false,
              stepTwo: true,
            },
          },
        }));
      } else {
        setChangeFlow((prevState) => ({
          ...prevState,
          modalFlow: {
            ...modalFlow,
            error: true,
            restartSubscription: {
              ...restartSubscription,
              stepOne: false,
            },
          },
        }));
      }
    } catch {
      setChangeFlow((prevState) => ({
        ...prevState,
        modalFlow: {
          ...modalFlow,
          error: true,
          restartSubscription: {
            ...restartSubscription,
            stepOne: false,
          },
        },
      }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Styled.RestartHeaderStack gap={1.5}>
          <Text size="2xl" weight="bold">
            <FormattedMessage id="dashboard.subscriptions.restart.title" />
          </Text>
          <Text size="md" color="secondary">
            <FormattedMessage id="dashboard.subscriptions.restart.subtitle" />
          </Text>
        </Styled.RestartHeaderStack>

        <Styled.SummaryCard>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack gap={0.5}>
              <Styled.ServiceNameText size="sm" weight="bold">
                {serviceDisplayName}
              </Styled.ServiceNameText>
              <Text size="sm" color="secondary">
                {businessEntityName}
              </Text>
            </Stack>
            <Stack direction="row" alignItems="baseline" gap={0.5}>
              <Text size="lg" weight="bold">
                {`$${subscription?.price}`}
              </Text>
              <Text size="xs" color="secondary">
                {`/ ${periodLabel}`}
              </Text>
            </Stack>
          </Stack>

          <Styled.SummaryDivider />

          <Stack gap={0.5}>
            <Text size="xs" weight="bold">
              <FormattedMessage id="dashboard.subscriptions.restart.nextPayment" />
            </Text>
            <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Text size="sm">{subscription?.termEndDate}</Text>
              <Text size="xs" color="secondary">
                <FormattedMessage id="dashboard.subscriptions.restart.nextPaymentNote" />
              </Text>
            </Stack>
          </Stack>

          <Styled.PaymentMethodStack gap={0.5}>
            <Text size="xs" weight="bold">
              <FormattedMessage id="dashboard.subscriptions.restart.paymentMethod" />
            </Text>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Text size="sm">{cardInfo}</Text>
              <Styled.ChangePaymentButton
                variant="text"
                size="small"
                to={ROUTES.billingPaymentMethods}
                clickstreamProps={{ elementName: 'restart_change_payment' }}
              >
                <FormattedMessage id="dashboard.subscriptions.restart.changePayment" />
              </Styled.ChangePaymentButton>
            </Stack>
          </Styled.PaymentMethodStack>
        </Styled.SummaryCard>

        <Stack gap={0.5}>
          <Styled.ConsentFormControlLabel
            control={
              <Checkbox
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                data-testid="accept-restart-terms"
              />
            }
            label={
              <Text component="span" size="sm">
                <FormattedMessage id="dashboard.subscriptions.restart.consent" />
              </Text>
            }
          />
          <Box pl={4}>
            <Text size="xs" color="tertiary">
              <FormattedMessage id="dashboard.subscriptions.renewalConsent" />
            </Text>
          </Box>
        </Stack>
      </DialogContent>

      <Styled.RestartDialogActions>
        <Styled.SubmitButton
          variant="contained"
          disabled={!consentChecked || submitting}
          onClick={handleSubmit}
          clickstreamProps={{ elementName: 'restart_subscription_submit' }}
          data-testid="restart-button"
        >
          {submitting ? (
            <LoadingSpinner size={20} aria-label="Submitting" />
          ) : (
            formatMessage({ id: 'dashboard.subscriptions.restart.submit' })
          )}
        </Styled.SubmitButton>
      </Styled.RestartDialogActions>
    </>
  );
}

ConfirmRestartSubscription.propTypes = {
  subscription: PropTypes.shape({
    orderBillingItemUuid: PropTypes.string,
    price: PropTypes.number,
    period: PropTypes.string,
    termEndDate: PropTypes.string,
  }).isRequired,
  businessEntityName: PropTypes.string,
  serviceDisplayName: PropTypes.string,
};
